import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EventCard from "@/components/base/EventCard";
import JsonLd from "@/components/base/JsonLd";
import RelationshipMap from "@/components/feature/RelationshipMap";
import usePageMeta from "@/hooks/usePageMeta";
import { events, categories } from "@/mocks/gossip";
import EventTimeline from "./components/EventTimeline";
import EvidenceList from "./components/EvidenceList";

const sections = [
  { id: "background", label: "前因后果" },
  { id: "timeline", label: "完整时间线" },
  { id: "evidence", label: "实锤整理" },
  { id: "relations", label: "关系图解码" },
];

export default function EventPage() {
  const { id } = useParams<{ id: string }>();
  const event = events.find((e) => e.id === id);

  usePageMeta(event ? `${event.title} - 完整时间线复盘` : "复盘档案未找到", {
    description: event?.summary ?? "该复盘档案不存在或已下线。",
    keywords: event ? `${event.tags.join(",")},91吃瓜网,时间线复盘` : "91吃瓜网",
    canonicalPath: `/event/${id}`,
    ogType: "article",
    ogImage: event?.cover,
  });

  if (!event) {
    return (
      <>
        <SiteHeader />
        <main className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">
          <i className="ri-file-unknow-line text-4xl text-foreground-400"></i>
          <h1 className="mt-4 font-heading font-bold text-2xl text-foreground-950">
            这篇复盘档案不存在
          </h1>
          <p className="mt-2 font-label text-sm text-foreground-600">
            可能链接有误，或者该事件已经下线。
          </p>
          <Link
            to="/events"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
          >
            返回复盘档案库
          </Link>
        </main>
        <SiteFooter />
      </>
    );
  }

  const cat = categories.find((c) => c.slug === event.categorySlug);
  const related = events.filter((e) => e.id !== event.id).slice(0, 3);
  const nameOf = (nodeId: string) => event.relations.find((n) => n.id === nodeId)?.name ?? nodeId;

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: event.title,
    description: event.summary,
    image: event.cover,
    datePublished: event.date,
    dateModified: event.date,
    articleSection: event.category,
    keywords: event.tags.join(","),
    inLanguage: "zh-CN",
    author: { "@type": "Organization", name: "91吃瓜网" },
    publisher: { "@type": "Organization", name: "91吃瓜网" },
    about: event.relations.map((r) => ({ "@type": "Thing", name: r.name })),
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://microtransport.cn/" },
      { "@type": "ListItem", position: 2, name: "复盘档案", item: "https://microtransport.cn/events" },
      {
        "@type": "ListItem",
        position: 3,
        name: event.category,
        item: `https://microtransport.cn/category/${event.categorySlug}`,
      },
      { "@type": "ListItem", position: 4, name: event.title },
    ],
  };

  return (
    <>
      <JsonLd data={[articleJsonLd, breadcrumbJsonLd]} />
      <SiteHeader />
      <main className="w-full bg-background-50">
        <div className="w-full px-4 md:px-8 pt-6">
          <Breadcrumb
            items={[
              { name: "首页", to: "/" },
              { name: "复盘档案", to: "/events" },
              { name: event.category, to: `/category/${event.categorySlug}` },
              { name: event.title },
            ]}
          />
        </div>

        <header className="w-full px-4 md:px-8 pt-6 pb-8">
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to={`/category/${event.categorySlug}`}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-3 py-1.5 font-label text-xs text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className={`${cat?.icon ?? "ri-price-tag-3-line"}`}></i>
              {event.category}
            </Link>
            <span className="inline-flex items-center gap-1.5 font-label text-xs text-foreground-500">
              <i className="ri-calendar-line"></i>
              最后更新 {event.date}
            </span>
            <span className="inline-flex items-center gap-1.5 font-label text-xs text-foreground-500">
              <i className="ri-fire-line text-accent-600"></i>
              热度 {event.heat}
            </span>
            <span className="inline-flex items-center gap-1.5 font-label text-xs text-foreground-500">
              <i className="ri-chat-3-line"></i>
              {event.comments} 条讨论
            </span>
          </div>

          <h1 className="mt-4 font-heading font-black text-3xl md:text-[42px] leading-[1.2] text-foreground-950 max-w-4xl">
            {event.title}
          </h1>
          <p className="mt-4 font-label text-base md:text-lg leading-relaxed text-foreground-600 max-w-3xl">
            {event.subtitle}
          </p>

          <div className="mt-5 flex flex-wrap gap-2">
            {event.tags.map((t) => (
              <span
                key={t}
                className="rounded-full border border-background-300 bg-background-100 px-3 py-1 font-label text-xs text-foreground-700"
              >
                # {t}
              </span>
            ))}
          </div>
        </header>

        <div className="w-full px-4 md:px-8">
          <div className="relative overflow-hidden rounded-lg border border-background-300 bg-background-200">
            <div className="w-full aspect-[16/9] md:aspect-[21/9]">
              <img
                src={event.cover}
                alt={`${event.title} 事件线索示意图`}
                className="w-full h-full object-top object-cover"
              />
            </div>
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/50 to-transparent"></div>
            <p className="absolute bottom-4 left-4 right-4 font-label text-xs text-background-100">
              图为事件线索示意，非当事人现场画面
            </p>
          </div>
        </div>

        <div className="w-full px-4 md:px-8 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <article className="lg:col-span-8 flex flex-col gap-12">
              {/* 前因后果 */}
              <section id="background" className="scroll-mt-24">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 flex items-center gap-3">
                  <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
                  前因后果
                </h2>
                <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                    <span className="inline-flex items-center gap-1.5 font-label text-xs text-secondary-700">
                      <i className="ri-book-open-line"></i>背景
                    </span>
                    <p className="mt-2 font-label text-sm leading-relaxed text-foreground-700">
                      {event.background}
                    </p>
                  </div>
                  <div className="rounded-lg border border-primary-200 bg-primary-50 p-5">
                    <span className="inline-flex items-center gap-1.5 font-label text-xs text-primary-700">
                      <i className="ri-flashlight-line"></i>导火索
                    </span>
                    <p className="mt-2 font-label text-sm leading-relaxed text-foreground-700">
                      {event.cause}
                    </p>
                  </div>
                  <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                    <span className="inline-flex items-center gap-1.5 font-label text-xs text-accent-800">
                      <i className="ri-flag-2-line"></i>当前状态
                    </span>
                    <p className="mt-2 font-label text-sm leading-relaxed text-foreground-700">
                      {event.outcome}
                    </p>
                  </div>
                </div>
              </section>

              {/* 时间线 */}
              <section id="timeline" className="scroll-mt-24">
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 flex items-center gap-3">
                    <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
                    完整时间线
                  </h2>
                  <span className="font-label text-xs text-foreground-500">
                    共 {event.timeline.length} 个节点 · 从 {event.timeline[0].time} 到{" "}
                    {event.timeline[event.timeline.length - 1].time}
                  </span>
                </div>
                <div className="mt-6">
                  <EventTimeline nodes={event.timeline} />
                </div>
              </section>

              {/* 实锤整理 */}
              <section id="evidence" className="scroll-mt-24">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 flex items-center gap-3">
                  <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
                  实锤整理
                </h2>
                <p className="mt-3 font-label text-sm text-foreground-600">
                  以下材料按可信度分为直接证据、间接证据、旁证与待核实四档，请结合等级判断。
                </p>
                <div className="mt-5">
                  <EvidenceList items={event.evidence} />
                </div>
              </section>

              {/* 关系图 */}
              <section id="relations" className="scroll-mt-24">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 flex items-center gap-3">
                  <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
                  关系图解码
                </h2>
                <p className="mt-3 font-label text-sm text-foreground-600">
                  每条连线都对应上文中的一个时间节点，未在正文出现的关系不会出现在图中。
                </p>
                <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5 md:p-8">
                  <RelationshipMap nodes={event.relations} links={event.links} />
                </div>

                <div className="mt-5 overflow-hidden rounded-lg border border-background-200">
                  <table className="w-full border-collapse text-left">
                    <caption className="sr-only">事件关系连线明细</caption>
                    <thead>
                      <tr className="bg-background-100">
                        <th scope="col" className="px-4 py-3 font-label text-xs text-foreground-600">关系方 A</th>
                        <th scope="col" className="px-4 py-3 font-label text-xs text-foreground-600">关系</th>
                        <th scope="col" className="px-4 py-3 font-label text-xs text-foreground-600">关系方 B</th>
                      </tr>
                    </thead>
                    <tbody>
                      {event.links.map((l, idx) => (
                        <tr key={idx} className="border-t border-background-200">
                          <td className="px-4 py-3 font-label text-sm text-foreground-800">{nameOf(l.from)}</td>
                          <td className="px-4 py-3">
                            <span
                              className={`rounded-full px-2.5 py-1 font-label text-[11px] ${
                                l.tone === "conflict"
                                  ? "bg-secondary-200 text-secondary-900"
                                  : l.tone === "positive"
                                  ? "bg-accent-100 text-accent-800"
                                  : "bg-background-200 text-foreground-700"
                              }`}
                            >
                              {l.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-label text-sm text-foreground-800">{nameOf(l.to)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="rounded-lg border border-dashed border-background-300 bg-background-100 p-5">
                <h2 className="font-heading font-semibold text-base text-foreground-950 flex items-center gap-2">
                  <i className="ri-information-line text-primary-600"></i>
                  修订与更正说明
                </h2>
                <p className="mt-2 font-label text-sm leading-relaxed text-foreground-600">
                  本文按公开信息整理，人物与事件为示例内容。若你发现某个节点与可核实的事实不符，欢迎通过页脚邮箱提供依据，我们会在复核后更新本页并记录修订时间。
                </p>
              </section>
            </article>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24 flex flex-col gap-5">
                <nav aria-label="本页目录" className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <h2 className="font-heading font-bold text-sm text-foreground-950">本页目录</h2>
                  <ul className="mt-3 space-y-1.5">
                    {sections.map((s) => (
                      <li key={s.id}>
                        <a
                          href={`#${s.id}`}
                          className="flex items-center gap-2 rounded-md px-2.5 py-2 font-label text-sm text-foreground-700 hover:bg-background-200 hover:text-primary-700 transition-colors cursor-pointer"
                        >
                          <i className="ri-subtract-line text-foreground-400"></i>
                          {s.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>

                <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <h2 className="font-heading font-bold text-sm text-foreground-950">事件速览</h2>
                  <dl className="mt-4 space-y-3 font-label text-sm">
                    <div className="flex items-center justify-between">
                      <dt className="text-foreground-500">所属分类</dt>
                      <dd className="text-foreground-900">{event.category}</dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-foreground-500">时间节点</dt>
                      <dd className="text-foreground-900">{event.timeline.length} 个</dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-foreground-500">实锤材料</dt>
                      <dd className="text-foreground-900">{event.evidence.length} 条</dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-foreground-500">关系节点</dt>
                      <dd className="text-foreground-900">{event.relations.length} 个</dd>
                    </div>
                    <div className="flex items-center justify-between">
                      <dt className="text-foreground-500">阅读时长</dt>
                      <dd className="text-foreground-900">约 {event.readMin} 分钟</dd>
                    </div>
                  </dl>
                </div>

                <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <h2 className="font-heading font-bold text-sm text-foreground-950">相关复盘推荐</h2>
                  <div className="mt-3 flex flex-col gap-3">
                    {related.map((r) => (
                      <EventCard key={r.id} event={r} variant="compact" />
                    ))}
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>

        <section className="w-full border-t border-background-200 bg-background-100 py-12 md:py-16">
          <div className="w-full px-4 md:px-8">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
              继续看其他深扒复盘
            </h2>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((r) => (
                <EventCard key={r.id} event={r} />
              ))}
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </>
  );
}